import { defineStore } from 'pinia'
import * as quotationApi from '../services/api/quotation'
import { IApproval } from './staff'

export interface IQuotationData {
  id: number
  is_urgent: boolean
  status: string
  type: string
  note: string
  reason: string
  approvals: Array<IApproval>
  request: string
  condition: string
  get_in_at: string
  get_out_at: string
  amount: number
  has_insurance: boolean
  assignee_id: number
  creator_id: number
  customer_id: number
  quote_lines: Array<any>
  car: any
  customer: any
  created_at: string
  _creator: string
}

export const useQuotationStore = defineStore('quotation', {
  state: () => ({
    quotation: {
      id: 0,
      is_urgent: false,
      status: '',
      type: '',
      note: '',
      reason: '',
      approvals: [],
      request: '',
      condition: '',
      get_in_at: '',
      get_out_at: '',
      amount: 0,
      has_insurance: false,
      assignee_id: 0,
      creator_id: 0,
      customer_id: 0,
      quote_lines: [],
      customer: null,
      car: null,
      created_at: '',
      _creator: '',
    } as IQuotationData,
    isLoading: false,
  }),

  actions: {
    setQuotation(data: IQuotationData) {
      this.quotation = data
    },

    setQuoteLine(data: any) {
      const quote_line = this.quotation.quote_lines.find((l: any) => l.id === data.id)
      if (quote_line) {
        Object.assign(quote_line, data)
      } else {
        this.quotation.quote_lines.push(data)
      }
    },

    updateStatus(status: string) {
      this.quotation.status = status
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getQuotation(quotation_id: number) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.get(quotation_id)
        this.setQuotation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateQuotation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.update(this.quotation.id, payload)
        this.setQuotation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectQuotation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.reject(this.quotation.id, payload)
        this.setQuotation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeQuotation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.remake(this.quotation.id, payload)
        this.setQuotation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveQuotation(params: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.approve(this.quotation.id, params)
        this.setQuotation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updatePriceInsurance(params: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.updatePriceInsurance(this.quotation.id, params)
        this.setQuotation(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createQuoteLine(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.createQuoteLine(this.quotation.id, payload)
        this.setQuoteLine(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateQuoteLine(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.updateQuoteLine(payload.id, payload)
        this.setQuoteLine(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async exportQuotation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.exportQuotation(this.quotation.id, payload)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyQuotation(params: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.apply(this.quotation.id, params)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useQuotationListStore = defineStore('quotationList', {
  state: () => ({
    quotations: [] as IQuotationData[],
    quotationsByCar: [] as IQuotationData[],
    count: 0,
    total: 0,
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addQuotation(data: IQuotationData) {
      this.quotations.push(data)
    },

    setQuotations(data: IQuotationData[]) {
      this.quotations = data
    },

    setQuotationsByCar(data: IQuotationData[]) {
      this.quotationsByCar = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countQuotations() {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createQuotation(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.create(payload)
        this.addQuotation(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listQuotations(params: any) {
      try {
        this.setLoading(true)
        const result = await quotationApi.list(params)
        const { data, total } = result as any
        this.setQuotations(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllQuotations(params: any) {
      try {
        this.setLoading(true)
        const result = await quotationApi.listAll(params)
        const { data, total } = result as any
        this.setQuotations(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listQuotationsByCar(params: any) {
      // Note: 'params' will be used when the actual API call is implemented
      try {
        this.setLoading(true)
        // TODO: Replace with actual API call when available
        const result = await quotationApi.list(params)
        const { data, total } = result as any
        this.setQuotationsByCar(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async exportSalary(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await quotationApi.exportSalary(payload)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
